import { faClose, faArrowDownAZ, faArrowUp19 } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Flex, Input, Tag, Tooltip } from 'antd';
import Weight from 'components/controls/Weight';
import { keyBy, sortBy } from 'lodash';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getScoreColor } from 'utils/ranking';

import './StakeholderImpactsTab.sass';

const IMPACT_RANK: Record<string, number> = { high: 3, medium: 2, low: 1 };

interface StakeholderImpactsTabProps {
	selection: any;
	stakeholders: any[];
	impactedFieldName: 'impactedStakes' | 'impactedIndicators' | 'impactedRisks';
	targetIdFieldName: 'stakeId' | 'indicatorId' | 'riskId';
	entityTypeLabel?: string;
	createImpact: (stakeholderId: string, targetId: string) => Promise<any> | void;
	updateImpactStrength: (impact: any, strength: number) => Promise<any> | void;
	deleteImpact: (id: string, stakeholderId: string) => Promise<any> | void;
	onClose?: () => void;
}

export const StakeholderImpactsTab = (props: StakeholderImpactsTabProps) => {
	const { stakeholders = [], impactedFieldName, targetIdFieldName, entityTypeLabel } = props;
	const { t } = useTranslation();
	const [searchTerm, setSearchTerm] = useState<string>('');
	const [sortMode, setSortMode] = useState<'name' | 'importance'>('name');
	const [polarityFilter, setPolarityFilter] = useState<{ positive: boolean; negative: boolean }>({
		positive: true,
		negative: true
	});

	const selectionId = props.selection?.id;
	const impactedStakeholders = stakeholders.filter(
		(s) => s[impactedFieldName]?.some((imp: any) => imp[targetIdFieldName] === selectionId)
	);
	const indexedStakeholders = keyBy(impactedStakeholders, 'id');

	const term = (searchTerm || '').toLowerCase();
	const filteredStakeholders = stakeholders.filter(
		(s) => {
			const isPositive = typeof s.y === 'number' && s.y > 0;
			const isNegative = typeof s.y === 'number' && s.y < 0;
			if (!polarityFilter.positive && isPositive) return false;
			if (!polarityFilter.negative && isNegative) return false;
			return (
				(s.name || '').toLowerCase().includes(term) ||
				(s.description || '').toLowerCase().includes(term)
			);
		}
	);

	const sortedStakeholders = sortMode === 'name'
		? sortBy(filteredStakeholders, (s) => (s.name || '').toLowerCase())
		: sortBy(filteredStakeholders, (s) => -(IMPACT_RANK[s.impact] || 0));

	return (
		<div className="StakeholderImpactsTab">
			<Flex vertical>
				<Flex className="mb-1">
					<div className="w-100 text-left">
						<div className="title">{props.selection?.name}</div>
						{entityTypeLabel && <small>{entityTypeLabel}</small>}
					</div>
					{props.onClose && (
						<Tooltip title={t('actions.close')} placement="left">
							<Button
								shape="circle"
								className="mini ml-auto"
								style={{ marginRight: '-0.9rem' }}
								onClick={() => props.onClose && props.onClose()}
							>
								<FontAwesomeIcon icon={faClose} />
							</Button>
						</Tooltip>
					)}
				</Flex>
			</Flex>

			<Input
				onChange={(e) => setSearchTerm(e.target.value)}
				value={searchTerm}
				placeholder={t('actions.search')}
				className="SearchInput w-100"
			/>

			<Flex align="center" gap={8} className="mt-2">
				<Tooltip title={t('actions.sort_by_name')}>
					<Button
						size="small"
						type={sortMode === 'name' ? 'primary' : 'default'}
						icon={<FontAwesomeIcon icon={faArrowDownAZ} />}
						onClick={() => setSortMode('name')}
					/>
				</Tooltip>
				<Tooltip title={t('actions.sort_by_importance')}>
					<Button
						size="small"
						type={sortMode === 'importance' ? 'primary' : 'default'}
						icon={<FontAwesomeIcon icon={faArrowUp19} />}
						onClick={() => setSortMode('importance')}
					/>
				</Tooltip>
				<div style={{ flex: 1 }} />
				<Tooltip title={t('actions.filter_positive')}>
					<Button
						size="small"
						type={polarityFilter.positive ? 'primary' : 'default'}
						onClick={() => setPolarityFilter((p) => ({ ...p, positive: !p.positive }))}
					>
						{t('models.item.levels.positive_short')}
					</Button>
				</Tooltip>
				<Tooltip title={t('actions.filter_negative')}>
					<Button
						size="small"
						type={polarityFilter.negative ? 'primary' : 'default'}
						onClick={() => setPolarityFilter((p) => ({ ...p, negative: !p.negative }))}
					>
						{t('models.item.levels.negative_short')}
					</Button>
				</Tooltip>
			</Flex>

			<Flex vertical className="mt-3 mb-4">
				<div className="text-left">
					{sortedStakeholders && sortedStakeholders.length ? (
						sortedStakeholders.map((s: any, index) => {
							const existingImpact = indexedStakeholders[s.id]
								&& indexedStakeholders[s.id][impactedFieldName]?.find(
									(imp: any) => imp[targetIdFieldName] === selectionId
								);

							return (
								<Tag.CheckableTag
									key={s.id || index}
									checked={!!existingImpact?.id}
									onClick={async () => {
										if (!existingImpact || !existingImpact.id) {
											await props.createImpact(s.id, selectionId);
										} else if (existingImpact.strength < 3) {
											await props.updateImpactStrength(
												existingImpact,
												existingImpact.strength + 1
											);
										} else {
											await props.deleteImpact(existingImpact.id, s.id);
										}
									}}
									className="ImpactedStake tag"
								>
									<div
										className={'Trend v' + s.trend}
										style={
											s.trend == '50'
												? {}
												: { background: getScoreColor(s.trend) }
										}
									></div>
									<span className="tag__label" title={s.name}>{s.name}</span>
									{existingImpact ? (
										<Weight onClick={() => { }}>
											{existingImpact.strength}
										</Weight>
									) : null}
								</Tag.CheckableTag>
							);
						})
					) : (
						<div
							className="text-center"
							style={{ color: 'var(--ant-color-text-tertiary)' }}
						>
							{t('models.item.no_x')}
						</div>
					)}
				</div>
			</Flex>
		</div>
	);
};
